import { useEffect, useState } from 'react'
import { GitBranch } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { toast } from '../../lib/shadcn/sonner'
import { useUpdateApplicationStatus, useResendStageEmail } from '../../hooks/backend/interviews'

// All valid driver application stages, in workflow order.
const STAGES = [
  'submitted',
  'rejected_score',
  'interview_1_pending',
  'interview_1_booked',
  'interview_1_no_show',
  'interview_1_failed',
  'interview_2_pending',
  'interview_2_booked',
  'interview_2_failed',
  'offer_pending',
  'offer_accepted',
  'onboarding_form_pending',
  'contract_pending',
  'contract_signed',
  'induction_pending',
  'induction_acknowledged',
  'ready_to_activate',
  'active',
  'withdrawn',
] as const

// Stages that trigger a standard applicant email, mapped to a short
// description of what gets sent. Stages absent here have no standard email.
const EMAILABLE_STAGES: Record<string, string> = {
  interview_1_pending: 'Interview 1 invite',
  interview_1_failed: 'Interview 1 result (not successful)',
  interview_2_pending: 'Passed interview 1, final interview invite',
  interview_2_failed: 'Final interview result (not successful)',
  offer_pending: 'Employment offer',
  onboarding_form_pending: 'Onboarding welcome',
  induction_pending: 'Induction pack',
}

// Sentence-case a stage code: underscores to spaces, capitalise the first
// letter only, keeping round numbers intact (e.g. "Interview 1 booked").
function humanizeStage(code: string): string {
  const spaced = code.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function StageControl({
  applicationId,
  currentStatus,
  canAct,
  onSaved,
}: {
  applicationId: string
  currentStatus: string
  canAct: boolean
  onSaved: () => void
}) {
  const { trigger } = useUpdateApplicationStatus()
  const { trigger: triggerResend } = useResendStageEmail()
  const [choice, setChoice] = useState(currentStatus)
  const [reason, setReason] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [busy, setBusy] = useState(false)
  const [resendBusy, setResendBusy] = useState(false)

  // Reset the picker whenever the applicant or their stage changes.
  useEffect(() => {
    setChoice(currentStatus)
    setReason('')
  }, [applicationId, currentStatus])

  // Only some stages have a standard email; the checkbox is hidden otherwise.
  const emailDescription = EMAILABLE_STAGES[choice]
  const canEmail = emailDescription !== undefined

  // Default to unticked whenever the applicant or selected stage changes.
  useEffect(() => {
    setSendEmail(false)
  }, [applicationId, choice])

  const changed = choice !== currentStatus

  // Resend is available only for the applicant's CURRENT stage (not the
  // picker choice), and only when that stage has a standard email.
  const currentEmailDescription = EMAILABLE_STAGES[currentStatus]
  const canResend = canAct && currentEmailDescription !== undefined

  const handleResend = async () => {
    setResendBusy(true)
    try {
      await triggerResend({ application_id: applicationId }).result
      toast.success('Email re-queued')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not resend email', { description: msg })
    } finally {
      setResendBusy(false)
    }
  }

  const handleSave = async () => {
    if (!changed) return
    const trimmed = reason.trim()
    setBusy(true)
    try {
      await trigger({
        application_id: applicationId,
        new_status: choice,
        reason: trimmed.length ? trimmed : null,
        send_email: canEmail ? sendEmail : false,
      }).result
      toast.success(`Stage changed to ${humanizeStage(choice)}`)
      setReason('')
      onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not change stage', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-[hsl(var(--brand))]" />
        <h3 className="text-sm font-semibold text-foreground">Pipeline stage</h3>
      </div>

      {!canAct ? (
        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current stage</p>
          <p className="text-sm font-medium text-foreground">{humanizeStage(currentStatus)}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`stage-${applicationId}`} className="text-xs font-medium text-muted-foreground">
              Stage
            </Label>
            <Select value={choice} onValueChange={setChoice} disabled={busy}>
              <SelectTrigger id={`stage-${applicationId}`} className="w-full sm:max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {humanizeStage(code)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor={`stage-reason-${applicationId}`}
              className="text-xs font-medium text-muted-foreground"
            >
              Reason (optional)
            </Label>
            <Textarea
              id={`stage-reason-${applicationId}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the stage changing?"
              disabled={busy}
              rows={2}
            />
          </div>

          {canEmail && (
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
              <Checkbox
                id={`stage-email-${applicationId}`}
                checked={sendEmail}
                onCheckedChange={(v) => setSendEmail(v === true)}
                disabled={busy}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label
                  htmlFor={`stage-email-${applicationId}`}
                  className="text-xs font-medium text-foreground"
                >
                  Also email the applicant the standard message for this stage
                </Label>
                <p className="text-[11px] text-muted-foreground">Sends: {emailDescription}</p>
              </div>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={!changed || busy}
            className="h-11 w-full justify-center sm:h-9 sm:w-auto"
          >
            {busy && <Spinner className="h-4 w-4" />}
            Save stage
          </Button>

          {canResend && (
            <div className="space-y-1.5 border-t border-border pt-3">
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={resendBusy}
                className="h-11 w-full justify-center sm:h-9 sm:w-auto"
              >
                {resendBusy && <Spinner className="h-4 w-4" />}
                Resend {currentEmailDescription} email
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Re-queues the standard email for the current stage. Does not change the stage.
              </p>
            </div>
          )}
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Advancing forward records any skipped stages in the history. No email is sent unless you tick
        the box above.
      </p>
    </section>
  )
}
